import { Observable } from 'rxjs/Observable';
import {
	IDelegationRequest,
	IReceivePasswordReq,
	IReceivePasswordResp,
	IUserIDRequest,
	IVerifyCodeRequest,
} from './http-requests';
import { AppEndpoints } from '../app.endpoints';
import { MetaData } from '../../globals/classes/urlBuilder';
import { HttpService } from '../../globals/providers/http.service';
import { IVerifyCodeResponse } from './http-responses';
import { AppSettings } from '../app.settings';

export interface IMailItem {
	mailAddress: string;
	logicalAddressKey: number;
}
export interface IGetPersonAccountUnlockOptionsResponse {
	emailAddress: string;
	hasActiveCreditCard: boolean;
	canUnlockWithPersonalInformation: boolean;
}

export interface IFetchUnlockEmailResponse {
	email: IMailItem;
}

export interface ISendUnlockEmailRequest {
	userId: string;
	resend: boolean;
}

export interface ISendUnlockEmailResponse {
	email: string;
	succeeded: boolean;
	sendEmailError: string;
}

export class SendUnlockEmailRequest implements ISendUnlockEmailRequest {
	public userId: string;
	public resend: boolean;

	constructor(req?: ISendUnlockEmailRequest) {
		let newReq: ISendUnlockEmailRequest = req || {
			userId: null,
			resend: null,
		};
		this.read(newReq);
	}
	private read(newReq: ISendUnlockEmailRequest) {
		this.resend = newReq.resend;
		this.userId = newReq.userId;
	}
}

export class SendUnlockEmailResponse implements ISendUnlockEmailResponse {
	public email: string;
	public succeeded: boolean;
	public sendEmailError: string;

	constructor(res?: ISendUnlockEmailResponse) {
		let newRes: ISendUnlockEmailResponse = res || {
			email: null,
			sendEmailError: null,
			succeeded: null,
		};
		this.read(newRes);
	}
	private read(newRes: ISendUnlockEmailResponse) {
		this.email = newRes.email;
		this.sendEmailError = newRes.sendEmailError;
		this.succeeded = newRes.succeeded;
	}
}

export class MailItem implements IMailItem {
	public mailAddress: string;
	public logicalAddressKey: number;

	constructor(mailItem?: IMailItem) {
		let newMailItem: IMailItem = mailItem || {
			mailAddress: null,
			logicalAddressKey: null,
		};

		this.read(newMailItem);
	}

	private read(newMailItem: IMailItem) {
		this.mailAddress = newMailItem.mailAddress;
		this.logicalAddressKey = newMailItem.logicalAddressKey;
	}
}

export class GetPersonAccountUnlockOptionsResponse implements IGetPersonAccountUnlockOptionsResponse {
	public emailAddress: string;
	public hasActiveCreditCard: boolean;
	public canUnlockWithPersonalInformation: boolean;

	constructor(resp?: IGetPersonAccountUnlockOptionsResponse) {
		let newResp: IGetPersonAccountUnlockOptionsResponse = resp || {
			emailAddress: null,
			hasActiveCreditCard: false,
			canUnlockWithPersonalInformation: true
		};

		this.read(newResp);
	}

	private read(newResp: IGetPersonAccountUnlockOptionsResponse) {
		this.emailAddress = newResp.emailAddress;
        this.hasActiveCreditCard = newResp.hasActiveCreditCard;
		this.canUnlockWithPersonalInformation = newResp.canUnlockWithPersonalInformation;
	}
}

export class FetchUnlockEmailResponse implements IFetchUnlockEmailResponse {
	public email: IMailItem;
	public hasActiveCreditCard: boolean;

	constructor(resp?: IFetchUnlockEmailResponse) {
		let newResp: IFetchUnlockEmailResponse = resp || {
			email: new MailItem()
		};

		this.read(newResp);
	}

	private read(newResp: IFetchUnlockEmailResponse) {
		this.email = new MailItem(newResp.email);
	}
}

export class GetPersonAccountUnlockOptions {
	/* Delegate Request */
	public static getPersonAccountUnlockOptions(request: IUserIDRequest): Observable<IGetPersonAccountUnlockOptionsResponse> {
		return HttpService.post<IGetPersonAccountUnlockOptionsResponse>(AppEndpoints.getPersonAccountUnlockOptions, request);
	}
}

export class UnlockAcountEmail {
	/* Delegate Request */
	public static fetchEmail(request: IUserIDRequest): Observable<IFetchUnlockEmailResponse> {
		if (AppSettings.skipDelegationRequest) {
			return HttpService.post<IFetchUnlockEmailResponse>(AppEndpoints.fetchEmail, request);
		} else {
			return UnlockAcountEmail.useDelegateRequestFetchEmail(AppEndpoints.fetchEmail, request);
		}
	}

	/* Delegate Request */
	public static sendUnlockEmail(
		request: ISendUnlockEmailRequest
	): Observable<ISendUnlockEmailResponse> {
		if (AppSettings.skipDelegationRequest) {
			return HttpService.post<ISendUnlockEmailResponse>(
				AppEndpoints.sendUnlockEmail,
				request
			);
		} else {
			return UnlockAcountEmail.useDelegateRequestSendUnlockEmail(
				AppEndpoints.sendUnlockEmail,
				request
			);
		}
	}

	/* Delegate Request */
	public static verifyCode(request: IVerifyCodeRequest): Observable<IVerifyCodeResponse> {
		if (AppSettings.skipDelegationRequest) {
			return HttpService.post<IVerifyCodeResponse>(AppEndpoints.verifyUnlockCode, request);
		} else {
			return UnlockAcountEmail.useDelegateRequestVerifyCode(
				AppEndpoints.verifyUnlockCode,
				request
			);
		}
	}

	/* FetchUnlockEmail */
	private static useDelegateRequestFetchEmail(
		endPointUrl: string,
		request: IUserIDRequest
	): Observable<IFetchUnlockEmailResponse> {
		return HttpService.post<IFetchUnlockEmailResponse>(
			AppEndpoints.delegateRequest,
			UnlockAcountEmail.setDelegationRequest(endPointUrl, request),
			false
		);
	}
	/* SendUnlockEmail */
	private static useDelegateRequestSendUnlockEmail(
		endPointUrl: string,
		request: ISendUnlockEmailRequest
	): Observable<ISendUnlockEmailResponse> {
		return HttpService.post<ISendUnlockEmailResponse>(
			AppEndpoints.delegateRequest,
			UnlockAcountEmail.setDelegationRequest(endPointUrl, request),
			false
		);
	}

	/* VerifyCode */
	private static useDelegateRequestVerifyCode(
		endPointUrl: string,
		request: IVerifyCodeRequest
	): Observable<IVerifyCodeResponse> {
		return HttpService.post<IVerifyCodeResponse>(
			AppEndpoints.delegateRequest,
			UnlockAcountEmail.setDelegationRequest(endPointUrl, request),
			false
		);
	}

	private static setDelegationRequest(endPointUrl: string, request: any): IDelegationRequest {
		return {
			audiences: 'LoginApi',
			endPointUrl: endPointUrl,
			body: new MetaData().wrap2Request(request),
		};
	}
}
